import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  Row,
  Col,
  Button,
  Form,
  Input,
  Avatar,
  Upload,
  message,
  Modal,
  Select,
  Space,
  Tag,
} from 'antd';
import {
  UserOutlined,
  CameraOutlined,
  MailOutlined,
  PhoneOutlined,
  SaveOutlined,
  CrownOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
} from '@ant-design/icons';
import { userService, addressService, storageUrl } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './CustomerDashboard.css';

const { Title, Paragraph, Text } = Typography;

const Profile = () => {
  const { user: authUser, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [form] = Form.useForm();
  const [addressForm] = Form.useForm();
  const [editing, setEditing] = useState(false);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);

  useEffect(() => {
    // Seed immediately from the logged-in user so the page never renders empty,
    // then refine with the fuller profile payload from the API.
    if (authUser) {
      setProfile((prev) => prev || authUser);
      form.setFieldsValue({
        full_name: authUser.name || authUser.full_name || '',
        email: authUser.email || '',
        phone_number: authUser.phone || authUser.phone_number || '',
      });
    }
    fetchProfile();
    fetchAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await userService.getProfile();
      const data = response.data?.data || response.data;
      if (!data) return;
      setProfile((prev) => ({ ...prev, ...data }));
      form.setFieldsValue({
        full_name: data.name || data.full_name || '',
        email: data.email || '',
        phone_number: data.phone || data.phone_number || '',
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchAddresses = async () => {
    try {
      const response = await addressService.getAddresses();
      const data = response.data?.data || response.data || [];
      setAddresses(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching addresses:', error);
    }
  };

  const handleAddAddress = async (values) => {
    setAddressLoading(true);
    try {
      await addressService.addAddress(values);
      message.success('Address added successfully');
      addressForm.resetFields();
      setAddressModalOpen(false);
      fetchAddresses();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to add address');
    } finally {
      setAddressLoading(false);
    }
  };

  const handleAvatarUpload = async (file) => {
    const formData = new FormData();
    formData.append('avatar', file);
    try {
      const response = await userService.uploadAvatar(formData);
      const data = response.data?.data || response.data;
      const imageUrl = storageUrl(data.profile_image || data?.data?.profile_image);
      setProfile((prev) => ({ ...prev, profile_image: imageUrl }));
      const stored = localStorage.getItem('authUser');
      if (stored) {
        const current = JSON.parse(stored);
        updateUser({ ...current, profile_image: imageUrl });
      }
      message.success('Avatar updated');
    } catch {
      message.error('Failed to upload avatar');
    }
    return false;
  };

  const handleUpdateProfile = async (values) => {
    setLoading(true);
    try {
      const response = await userService.updateProfile({
        name: values.full_name,
        phone: values.phone_number,
      });
      const updated = response.data?.data || response.data;
      const stored = localStorage.getItem('authUser');
      const current = stored ? JSON.parse(stored) : {};
      updateUser({
        ...current,
        id: updated.id,
        name: updated.name || updated.full_name,
        email: updated.email,
        user_type: updated.user_type,
        status: updated.status,
        profile_image: updated.profile_image,
      });
      message.success('Profile updated successfully');
      setEditing(false);
      fetchProfile();
    } catch (error) {
      message.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (!profile) {
    return (
      <div className="customer-page" style={{ padding: '0' }}>
        <div className="customer-page-header">
          <div
            className="customer-card-loading"
            style={{ height: 24, width: 200, marginBottom: 8 }}
          />
          <div className="customer-card-loading" style={{ height: 16, width: 300 }} />
        </div>
      </div>
    );
  }

  return (
    <div className="customer-page" style={{ padding: '0' }}>
      <div className="customer-page-header">
        <Title level={2}>My Profile</Title>
        <Paragraph>
          Manage your account details, delivery addresses, and preferences in one place.
        </Paragraph>
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24} md={8}>
          <Card
            className="customer-card animate-fade-slide-up animate-stagger-1"
            styles={{ body: { textAlign: 'center', padding: '32px 24px' } }}
          >
            <div className="customer-avatar-upload">
              <Avatar size={120} src={storageUrl(profile.profile_image)} icon={<UserOutlined />} />
            </div>
            <div style={{ marginTop: 16 }}>
              <Title level={4} style={{ color: '#2d5a3d', marginBottom: 4 }}>
                {profile.name || profile.full_name}
              </Title>
              <Text style={{ color: '#6b7280' }}>{profile.email}</Text>
            </div>
            <div style={{ marginTop: 12 }}>
              <Tag className="customer-tag-green">Active</Tag>
              <Tag
                className="customer-tag-green"
                style={{ background: '#e8f4fb', color: '#2e7eba', textTransform: 'capitalize' }}
              >
                {profile.user_type}
              </Tag>
            </div>
            {profile.has_membership && (
              <div
                style={{
                  marginTop: 16,
                  padding: '12px 16px',
                  background: 'linear-gradient(135deg, #e8f5e9, #a8e6cf)',
                  borderRadius: 12,
                  border: '1px solid #a8e6cf',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <CrownOutlined style={{ color: '#f1a134', fontSize: 20 }} />
                  <Text strong style={{ color: '#2d5a3d', fontSize: 15 }}>
                    Active Member
                  </Text>
                  <CheckCircleFilled style={{ color: '#52b788', marginLeft: 'auto' }} />
                </div>
                <div style={{ fontSize: 13, color: '#2d5a3d' }}>
                  {profile.membership?.days_remaining > 0 && (
                    <Text style={{ color: '#2d5a3d', display: 'block', marginBottom: 4 }}>
                      {profile.membership.days_remaining} days remaining
                    </Text>
                  )}
                  {profile.membership?.membership_end_date && (
                    <Text style={{ color: '#3d7a4d', display: 'block', fontSize: 12 }}>
                      Valid until{' '}
                      {new Date(profile.membership.membership_end_date).toLocaleDateString(
                        'en-US',
                        { year: 'numeric', month: 'long', day: 'numeric' },
                      )}
                    </Text>
                  )}
                </div>
              </div>
            )}
            {!profile.has_membership && profile.user_type === 'customer' && (
              <div
                style={{
                  marginTop: 16,
                  padding: '12px 16px',
                  background: '#f9fafb',
                  borderRadius: 12,
                  border: '1px solid #e5e7eb',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CrownOutlined style={{ color: '#d1d5db', fontSize: 20 }} />
                  <Text style={{ color: '#9ca3af', fontSize: 14 }}>No Active Membership</Text>
                  <CloseCircleFilled style={{ color: '#d1d5db', marginLeft: 'auto' }} />
                </div>
              </div>
            )}
            {(() => {
              const details = [
                { label: 'Phone', value: profile.phone || profile.phone_number },
                {
                  label: 'Loyalty Points',
                  value:
                    profile.loyalty_points !== undefined && profile.loyalty_points !== null
                      ? Number(profile.loyalty_points).toLocaleString()
                      : null,
                },
                { label: 'Referral Code', value: profile.referral_code },
                {
                  label: 'Member Since',
                  value: profile.created_at
                    ? new Date(profile.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                      })
                    : null,
                },
              ].filter((d) => d.value);
              if (details.length === 0) return null;
              return (
                <div
                  style={{
                    marginTop: 16,
                    padding: '4px 14px',
                    background: '#fafff5',
                    border: '1px solid #e8f0e8',
                    borderRadius: 12,
                    textAlign: 'left',
                  }}
                >
                  {details.map((d, i) => (
                    <div
                      key={d.label}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 12,
                        padding: '10px 0',
                        borderTop: i === 0 ? 'none' : '1px solid #eef4ee',
                      }}
                    >
                      <Text style={{ color: '#6b7280', fontSize: 13 }}>{d.label}</Text>
                      <Text strong style={{ color: '#2d5a3d', fontSize: 13, textAlign: 'right' }}>
                        {d.value}
                      </Text>
                    </div>
                  ))}
                </div>
              );
            })()}

            <Upload
              style={{ marginTop: 20 }}
              showUploadList={false}
              beforeUpload={handleAvatarUpload}
              accept="image/*"
            >
              <Button
                className="customer-btn-secondary"
                icon={<CameraOutlined />}
                style={{ height: 36, fontSize: 13 }}
              >
                Change Avatar
              </Button>
            </Upload>
          </Card>
        </Col>

        <Col xs={24} md={16}>
          <Card
            className="customer-card animate-fade-slide-up animate-stagger-2"
            title="Personal Information"
          >
            <Form form={form} layout="vertical" onFinish={handleUpdateProfile} disabled={!editing}>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item name="full_name" label="Full Name" rules={[{ required: true }]}>
                    <Input prefix={<UserOutlined />} style={{ borderRadius: 10 }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="email"
                    label="Email Address"
                    rules={[{ required: true, type: 'email' }]}
                  >
                    <Input prefix={<MailOutlined />} style={{ borderRadius: 10 }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="phone_number"
                    label="Phone Number"
                    rules={[{ pattern: /^\+?\d+$/, message: 'Please enter numbers and + only' }]}
                  >
                    <Input
                      type="tel"
                      onKeyPress={(e) =>
                        e.key !== '+' && !/[0-9]/.test(e.key) && e.preventDefault()
                      }
                      prefix={<PhoneOutlined />}
                      style={{ borderRadius: 10 }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {editing && (
                <div style={{ textAlign: 'right', marginTop: 24 }}>
                  <Space>
                    <Button className="customer-btn-secondary" onClick={() => setEditing(false)}>
                      Cancel
                    </Button>
                    <Button
                      className="customer-btn-primary"
                      htmlType="submit"
                      loading={loading}
                      icon={<SaveOutlined />}
                    >
                      Save Changes
                    </Button>
                  </Space>
                </div>
              )}
            </Form>

            {!editing && (
              <div style={{ textAlign: 'right' }}>
                <Button className="customer-btn-primary" onClick={() => setEditing(true)}>
                  Edit Profile
                </Button>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <Card
            className="customer-card animate-fade-slide-up animate-stagger-3"
            title="Delivery Addresses"
          >
            {addresses.length === 0 ? (
              <Paragraph style={{ color: '#6b7280' }}>No addresses saved yet.</Paragraph>
            ) : (
              addresses.map((address) => (
                <Card
                  key={address.id}
                  className="customer-card"
                  size="small"
                  style={{ marginBottom: 8, background: '#fafff5' }}
                >
                  <Text strong style={{ color: '#2d5a3d' }}>
                    {address.label || address.type || 'Address'}
                  </Text>
                  <br />
                  <Text>
                    {address.address_line1}
                    {address.address_line2 ? `, ${address.address_line2}` : ''}
                  </Text>
                  <br />
                  <Text type="secondary">{address.city}</Text>
                </Card>
              ))
            )}
            <Button
              className="customer-btn-primary"
              block
              style={{ marginTop: 16 }}
              onClick={() => {
                addressForm.setFieldsValue({
                  full_name: profile?.name || profile?.full_name || '',
                  phone_number: profile?.phone || profile?.phone_number || '',
                });
                setAddressModalOpen(true);
              }}
            >
              Add New Address
            </Button>
          </Card>
        </Col>
      </Row>

      <Modal
        title="Add New Address"
        open={addressModalOpen}
        onCancel={() => {
          addressForm.resetFields();
          setAddressModalOpen(false);
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={addressForm} layout="vertical" onFinish={handleAddAddress}>
          <Form.Item
            name="full_name"
            label="Full Name"
            rules={[{ required: true, message: 'Please enter your full name' }]}
          >
            <Input style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item
            name="phone_number"
            label="Phone Number"
            rules={[
              { required: true, message: 'Please enter your phone number' },
              { pattern: /^\+?\d+$/, message: 'Please enter numbers and + only' },
            ]}
          >
            <Input
              type="tel"
              onKeyPress={(e) => e.key !== '+' && !/[0-9]/.test(e.key) && e.preventDefault()}
              style={{ borderRadius: 10 }}
            />
          </Form.Item>
          <Form.Item
            name="address_line1"
            label="Address Line 1"
            rules={[{ required: true, message: 'Please enter your address' }]}
          >
            <Input style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item name="address_line2" label="Address Line 2 (optional)">
            <Input style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item name="landmark" label="Landmark (optional)">
            <Input style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item
            name="city"
            label="City"
            rules={[{ required: true, message: 'Please enter your city' }]}
          >
            <Input style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item name="address_type" label="Address Type">
            <Select style={{ borderRadius: 10 }}>
              <Select.Option value="home">Home</Select.Option>
              <Select.Option value="office">Office</Select.Option>
              <Select.Option value="other">Other</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="state" label="State">
            <Input style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item name="country" label="Country">
            <Input style={{ borderRadius: 10 }} />
          </Form.Item>
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  addressForm.resetFields();
                  setAddressModalOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button className="customer-btn-primary" htmlType="submit" loading={addressLoading}>
                Save Address
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default Profile;
